import type { FlagAction } from "@mnk/shared/models/flagAction";
import type { FlagColumn } from "@mnk/shared/models/flagColumn";
import type { FlagRow } from "@mnk/shared/models/flagRow";
import { type Quantity, newQuantity } from "@mnk/shared/models/quantity";
import { useState } from "react";
import { v7 as uuidv7 } from "uuid";
import * as XLSX from "xlsx";
import { buildFlag } from "./buildFlag";
import { buildFlagActions } from "./buildFlagActions";
import { parseDataRows } from "./parseDataRows";
import { detectEndCol, parseHeaderRows } from "./parseHeaderRows";
interface QuantityData {
  quantity: Quantity;
  flagRows: FlagRow[];
  flagColumns: FlagColumn[];
  flagActions: FlagAction[];
  projectId: string;
}

export const useQuantityUpload = () => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantityData, setQuantityData] = useState<Quantity | null>(null);

  /**
   * エクセルファイルの拡張子をチェックする
   */
  const isValidExcel = (file: File): boolean => {
    const validExtensions = [".xlsx"];
    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  /**
   * エクセルファイルからQuantityDataを構築する
   */
  const buildQuantity = (file: File, projectId: string): Quantity => {
    const name = file.name.replace(/\.[^/.]+$/, "");

    return newQuantity({
      projectId,
      name,
      id: uuidv7(),
    });
  };

  const parse = (file: File, projectId: string): Promise<QuantityData> => {
    const flagRows: FlagRow[] = [];
    const flagActions: FlagAction[] = [];
    const flagColumns: FlagColumn[] = [];

    return new Promise((resolve, reject) => {
      setProcessing(true);
      setError(null);

      // エクセルファイルの拡張子チェック
      if (!isValidExcel(file)) {
        const errorMessage = "エクセルファイル（.xlsx）のみアップロードできます。";
        setError(errorMessage);
        setProcessing(false);
        reject(errorMessage);
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            throw new Error("ファイルの読み込みに失敗しました");
          }

          const workbook = XLSX.read(data, { type: "binary" });
          const quantity = buildQuantity(file, projectId);
          setQuantityData(quantity);

          const { flagColumnMap } = parseHeaderRows(quantity.id, data);
          flagColumns.push(...Object.values(flagColumnMap).map((column) => column.flagColumn));

          for (const sheetName of workbook.SheetNames) {
            const worksheet = workbook.Sheets[sheetName];

            const endCol = detectEndCol(worksheet);
            const dataRows = parseDataRows(endCol, worksheet);

            let sortOrder = 0;
            for (const row of dataRows) {
              const { flagRow } = buildFlag(quantity.id, row, sortOrder);
              flagRows.push(flagRow);
              const { flagActions: builtFlagActions } = buildFlagActions(
                quantity.id,
                row,
                flagRow,
                flagColumnMap,
                sheetName,
              );
              flagActions.push(...builtFlagActions);
              sortOrder++;
            }
          }

          setProcessing(false);
          resolve({ quantity, flagRows, flagColumns, flagActions, projectId });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "不明なエラーが発生しました";
          setError(errorMessage);
          setProcessing(false);
          reject(errorMessage);
        }
      };

      reader.onerror = () => {
        setError("ファイルの読み込み中にエラーが発生しました");
        setProcessing(false);
        reject("ファイルの読み込み中にエラーが発生しました");
      };

      reader.readAsArrayBuffer(file);
    });
  };

  return {
    processing,
    error,
    setError,
    quantityData,
    parse,
  };
};
